import { ColumnLayout } from "@amzn/awsui-components-react";
import React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ValueWithLabel from "shared/programs/migration-acceleration-program/2024/fund-request/components/fields/ValueWithLabel";
import { display } from "shared/util/common/display";
import InfoLink from "shared/components/InfoLink";
import { IReadOnlyComponentProps } from "external/programs/migration-acceleration-program/2024/fund-request/types/SchemaTypes";
import { longLocalDateFormat } from "shared/util/common/date";
import ConditionalField from "shared/components/common/Conditional/ConditionalField";
import useFeatureFlag from "shared/util/hooks/useFeatureFlag";
import { FEATURE_FLAG_ENUMS } from "shared/util/constants/featureFlagValues";

const ReadOnlyFundingDetails = ({
  data,
  actions = <></>,
}: IReadOnlyComponentProps) => {
  const apidEnabled = useFeatureFlag(FEATURE_FLAG_ENUMS.APID_VALIDATION_ENABLED);
  if (!data) return <></>;
  const fundingDetails = data;
  const localCurrency = fundingDetails && fundingDetails.currency;

  return (
    <Container
      header={
        <Header
          variant="h2"
          info={
            <InfoLink
              onFollow={() => {
                //TODO: Implement help panel
              }}
            />
          }
          actions={actions}
        >
          Cash request details
        </Header>
      }
    >
      <ColumnLayout columns={3} variant="text-grid">
        <SpaceBetween size="l">
          <ValueWithLabel label="Currency">
            {display({ data: fundingDetails, key: "currency" })}
          </ValueWithLabel>
          {apidEnabled ? (
            <ValueWithLabel label="Account Profile ID">
              {display({ data: fundingDetails, key: "accountProfileId" })}
            </ValueWithLabel>
          ) : (
            <ValueWithLabel label="Invoice entity name">
              {display({ data: fundingDetails, key: "invoiceEntityName" })}
            </ValueWithLabel>
          )}
          {!apidEnabled && (
            <ValueWithLabel label="Apartment / suite / unit">
              {display({ data: fundingDetails, key: "invoiceRemitUnit" })}
            </ValueWithLabel>
          )}
          {!apidEnabled && (
            <ValueWithLabel label="Postal code">
              {display({ data: fundingDetails, key: "invoiceRemitPostalCode" })}
            </ValueWithLabel>
          )}
          <ValueWithLabel label="PO Issued">
            {display({
              data: fundingDetails,
              key: "poIssued",
              defaultValue: "No",
              format: (value) => (value ? "Yes" : "No"),
            })}
          </ValueWithLabel>
          <ValueWithLabel label="PO amount (USD)">
            {display({
              data: fundingDetails,
              key: "poUsdAmount",
            })}
          </ValueWithLabel>
        </SpaceBetween>
        <SpaceBetween size="l">
          <ValueWithLabel label={`Total project cost (${localCurrency})`}>
            {display({
              data: fundingDetails,
              key: "totalCostOfActivity",
            })}
          </ValueWithLabel>
          {!apidEnabled && (
            <ValueWithLabel label="Invoice remit country">
              {display({ data: fundingDetails, key: "invoiceRemitCountry" })}
            </ValueWithLabel>
          )}
          {!apidEnabled && (
            <ValueWithLabel label="City">
              {display({ data: fundingDetails, key: "invoiceRemitCity" })}
            </ValueWithLabel>
          )}
          <ValueWithLabel label="PO number">
            {display({ data: fundingDetails, key: "poNumber" })}
          </ValueWithLabel>
          <ValueWithLabel label="PO issued date">
            {display({
              data: fundingDetails,
              key: "poIssuedDate",
              format: (value) => longLocalDateFormat(value as string),
            })}
          </ValueWithLabel>
          <ValueWithLabel label={`Approved cash amount (${localCurrency})`}>
            {display({ data: fundingDetails, key: "approvedCashLocalAmount" })}
          </ValueWithLabel>
        </SpaceBetween>
        <SpaceBetween size="l">
          <ValueWithLabel
            label={`Total funds requested from AWS (${localCurrency})`}
          >
            {display({
              data: fundingDetails,
              key: "cashFundingAmount",
            })}
          </ValueWithLabel>
          {!apidEnabled && (
            <ValueWithLabel label="Street address">
              {display({ data: fundingDetails, key: "invoiceRemitStreet" })}
            </ValueWithLabel>
          )}
          {!apidEnabled && (
            <ValueWithLabel label="State / region / province">
              {display({ data: fundingDetails, key: "invoiceRemitState" })}
            </ValueWithLabel>
          )}
          <ValueWithLabel label="PO requisition number">
            {display({ data: fundingDetails, key: "poRequestNumber" })}
          </ValueWithLabel>
          <ValueWithLabel label={`PO amount  (${localCurrency})`}>
            {display({ data: fundingDetails, key: "poLocalAmount" })}
          </ValueWithLabel>
          <ConditionalField showField={localCurrency !== "USD"}>
            <ValueWithLabel label="USD Conversion Rate">
              {display({
                data: fundingDetails,
                key: "currencyConversionRateToUsd",
              })}
            </ValueWithLabel>
          </ConditionalField>
        </SpaceBetween>
      </ColumnLayout>
    </Container>
  );
};

export default ReadOnlyFundingDetails;
